<template>
  <div
    class="c-graph c-graph--stack"
    :class="{ 'is-error': isError, 'is-loading': isLoading || isDrawing }"
    ref="canvas"
  >
    <Loading v-if="isLoading || isDrawing" transparent />
    <template v-if="!isLoading && isError">
      <Error v-if="!hasKeyword" img="require" transparent>
        <template slot="title">
          指名キーワードを登録してください。
        </template>
      </Error>
      <Error v-else-if="!hasCompleteData" transparent>
        <template slot="title">
          データ取得中です。
        </template>
        <ul>
          <li v-if="!hasData">
            データの取得に<span class="c-text--accent">1時間程度</span
            >かかるため、<br />時間を置いてご確認ください。
          </li>
          <li v-else-if="!hasCompleteData">
            データがまだ完全には集まっていないため、<br />もう少し時間を置いてからご確認ください。
          </li>
        </ul>
      </Error>
    </template>
    <svg class="c-graph__svg" ref="svg">
      <defs ref="defs">
        <GraphMask id="stack" :size="size" />
      </defs>
      <g class="c-graph-bg" clip-path="url(#clipPath--stack)">
        <GraphBg
          :data="y.bg"
          direction="y"
          :scale="d3.y"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgY"
        />
      </g>
      <g class="c-graph-stack" clip-path="url(#clipPath--stack)" ref="gStack">
        <g ref="gStackMask"></g>
        <g ref="gStackBar"></g>
        <g ref="gStackLabel"></g>
      </g>
      <GraphFrame :size="size" hasLine />
      <g class="c-graph-axis">
        <GraphAxis
          :data="x.axis"
          position="bottom"
          :scale="d3.x"
          :scaleType="x.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisX"
        />
        <GraphAxis
          v-show="!isError"
          :data="y.axis"
          position="left"
          :scale="d3.y"
          :scaleType="y.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisY"
        />
      </g>
    </svg>
  </div>
</template>

<script>
import Error from '@/components/Error';
import GraphAxis from '@/components/GraphAxis';
import GraphBg from '@/components/GraphBg';
import GraphFrame from '@/components/GraphFrame';
import GraphMask from '@/components/GraphMask';
import Loading from '@/components/Loading';
import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: ['data', 'keywords', 'period', 'isLoading'],

  components: {
    Error,
    GraphAxis,
    GraphBg,
    GraphFrame,
    GraphMask,
    Loading
  },

  data() {
    return {
      d3: {
        stack: () => {},
        x: () => {},
        y: () => {}
      },
      el: {
        canvas: '',
        svg: '',
        gDefs: '',
        gStack: '',
        gStackMask: '',
        gStackBar: '',
        gStackLabel: ''
      },
      path: {
        data: [],
        resetData: []
      },
      size: {
        axisXH: 22,
        axisYW: {
          left: 20,
          right: 0
        },
        barW: 24,
        canvasH: 0,
        canvasW: 0,
        frameW: 1,
        graphH: 0,
        graphW: 0,
        graph: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        labelH: 18,
        offset: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        windowW: window.innerWidth
      },
      transition: {
        duration: 500,
        ease: d3.easeQuad
      },
      x: {
        axis: [],
        bg: [],
        domain: [],
        scaleType: 'timeMonth'
      },
      y: {
        axis: [],
        bg: [],
        domain: [],
        scaleType: 'number'
      },
      isDrawed: false,
      isDrawing: false
    };
  },

  computed: {
    hasCompleteData() {
      const months = d3.timeMonth.count(this.period[0], this.period[1]) + 1;
      return this.data.items.length === months;
    },
    hasData() {
      return this.data.items.length ? true : false;
    },
    hasKeyword() {
      return this.keywords.length ? true : false;
    },
    isError() {
      if (!this.hasCompleteData || !this.hasKeyword) {
        return true;
      }
      return false;
    }
  },

  watch: {
    data: {
      handler() {
        this.update();
      },
      deep: true
    },
    'size.windowW'() {
      this.resize();
    }
  },

  mounted() {
    this.setEl();
    this.setSize();
    this.update();
    this.setEvent();
  },

  methods: {
    update() {
      if (this.isLoading) {
        return;
      }
      this.isDrawing = true;
      this.setData();
      this.setScale();
      this.draw();
      this.isDrawed = true;
      this.isDrawing = false;
    },

    setEl() {
      this.el.canvas = this.$refs.canvas;
      this.el.svg = d3.select(this.$refs.svg);
      this.el.gDefs = d3.select(this.$refs.defs);
      this.el.gStack = d3.select(this.$refs.gStack);
      this.el.gStackMask = d3.select(this.$refs.gStackMask);
      this.el.gStackBar = d3.select(this.$refs.gStackBar);
      this.el.gStackLabel = d3.select(this.$refs.gStackLabel);
    },

    setSize() {
      this.size.canvasH = this.el.canvas.clientHeight;
      this.size.canvasW = this.el.canvas.clientWidth;

      this.size.graphH =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW * 2 -
        this.size.offset.top -
        this.size.offset.bottom;

      this.size.graphW =
        this.size.canvasW -
        this.size.axisYW.left -
        this.size.axisYW.right -
        this.size.frameW * 2 -
        this.size.offset.left -
        this.size.offset.right;

      this.size.graph.bottom =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW -
        this.size.offset.bottom;

      this.size.graph.left =
        this.size.offset.left + this.size.axisYW.left + this.size.frameW;

      this.size.graph.right =
        this.size.canvasW -
        this.size.axisYW.right -
        this.size.frameW -
        this.size.offset.right;

      this.size.graph.top = this.size.offset.top + this.size.frameW;
    },

    setEvent() {
      window.addEventListener(
        'resize',
        () => {
          this.size.windowW = window.innerWidth;
        },
        false
      );
    },

    setData() {
      this.path.data = d3.stack().keys(['general', 'nomination'])(
        this.data.items
      );

      this.x.domain = this.getDomainX();
      this.x.bg = this.getBgX();
      this.x.axis = this.getAxisX();

      this.y.axis = this.getAxisY();
      this.y.bg = this.getBgY();
      this.y.domain = this.getDomainY();
    },

    getDomainX() {
      let array = [
        utils.getStartDate(this.period[0], 'month'),
        utils.getStartDate(this.period[1], 'month')
      ];
      return array;
    },

    getBgX() {
      let array = d3[this.x.scaleType].range(
        this.x.domain[0],
        this.x.domain[1]
      );
      if (!array.length) {
        array = [this.x.domain[0]];
      }
      return array;
    },

    getAxisX() {
      let max = this.x.bg.length;
      return Math.min(max, 12);
    },

    getAxisY() {
      let max = d3.max(
        this.data.items.map(item => {
          return item.general + item.nomination;
        })
      );
      return utils.getTickValues(5, max + 1);
    },

    getBgY() {
      let array = d3.range(
        this.y.axis[1] / 2,
        this.y.axis[this.y.axis.length - 1],
        this.y.axis[1] / 2
      );
      return array;
    },

    getDomainY() {
      return [0, this.y.axis[this.y.axis.length - 1]];
    },

    setScale() {
      this.d3.x = d3
        .scaleTime()
        .domain(this.x.domain)
        .rangeRound([this.size.graph.left + 80, this.size.graph.right - 80]);

      this.d3.y = d3
        .scaleLinear()
        .domain(this.y.domain)
        .rangeRound([
          this.size.graph.bottom,
          this.size.graph.top + this.size.labelH //labelHを足すことで最上部のラベルが隠れないようにする
        ]);
    },

    draw() {
      this.drawPath();
      this.$refs.gAxisX.draw();
      this.$refs.gAxisY.draw();
      this.$refs.gBgY.draw();
    },

    attrStackMask(selection) {
      selection
        .attr('x', (d, i) => {
          return (
            this.d3.x(utils.getStartDate(d.data.monthJst, 'month')) -
            this.size.barW / 2
          );
        })
        .attr('y', (d, i) => {
          return this.d3.y(d[1]);
        })
        .attr('height', this.size.canvasH)
        .attr('width', this.size.barW)
        .attr('rx', 4)
        .attr('ry', 4);
    },

    attrStackBar(selection) {
      selection
        .attr('x', (d, i) => {
          return (
            this.d3.x(utils.getStartDate(d.data.monthJst, 'month')) -
            this.size.barW / 2
          );
        })
        .attr('y', (d, i) => {
          return this.d3.y(d[1]);
        })
        .attr('height', (d, i) => {
          return this.d3.y(d[0]) - this.d3.y(d[1]);
        })
        .attr('width', this.size.barW);
    },

    attrStackLabel(selection) {
      selection.attr('transform', (d, i, nodes) => {
        let index = Number(nodes[i].getAttribute('data-index')) + 1;
        let height = this.d3.y(d[0]) - this.d3.y(d[1]);
        let x =
          this.d3.x(utils.getStartDate(d.data.monthJst, 'month')) -
          this.size.barW / 2;
        let y = this.d3.y(d[1]) + height / 2 - this.size.labelH / 2;

        if (height < this.size.labelH) {
          y = y - (this.size.labelH - height) / 2;
        }
        if (y >= this.size.graph.bottom - this.size.labelH * index) {
          y = this.size.graph.bottom - this.size.labelH * index;
        }
        return `translate(${x}, ${y})`;
      });
    },

    attrStackLabelBg(selection) {
      selection
        .attr('x', 0)
        .attr('y', 0)
        .attr('height', this.size.labelH)
        .attr('width', this.size.barW);
    },

    attrStackLabelText(selection) {
      selection
        .attr('x', this.size.barW / 2)
        .attr('y', 13)
        .attr('text-anchor', 'middle')
        .text((d, i, nodes) => {
          return this.$options.filters.separateNumber(d[1] - d[0]);
        });
    },

    drawPath() {
      let transition = d3
        .transition()
        .duration(this.transition.duration)
        .ease(this.transition.ease);

      this.el.gStackMask
        .selectAll('clipPath')
        .data(this.path.data[1])
        .join(
          enter => {
            return enter
              .append('clipPath')
              .attr('id', (d, i) => {
                return 'stackClipPath' + i;
              })
              .call(enter => {
                enter.append('rect').call(this.attrStackMask);
              });
          },
          update => {
            return update
              .select('rect')
              .interrupt()
              .transition(transition)
              .call(this.attrStackMask);
          },
          exit => {
            return exit.remove();
          }
        );

      this.el.gStackBar
        .selectAll('g')
        .data(this.path.data)
        .join(
          enter => {
            return enter.append('g').attr('class', (d, i) => {
              return 'c-graph-stack__bar c-graph-stack__bar--' + d.key;
            });
          },
          update => {
            return update;
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('rect')
            .data(d)
            .join(
              enter => {
                return enter
                  .append('rect')
                  .attr('clip-path', (d, i) => {
                    return 'url(#stackClipPath' + i + ')';
                  })
                  .call(this.attrStackBar);
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrStackBar);
              },
              exit => {
                return exit.remove();
              }
            );
        });

      this.el.gStackLabel
        .selectAll('.c-graph-stack__label')
        .data(this.path.data)
        .join(
          enter => {
            return enter.append('g').attr('class', 'c-graph-stack__label');
          },
          update => {
            return update;
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('g')
            .data(d)
            .join(
              enter => {
                return enter
                  .append('g')
                  .attr('data-index', index)
                  .call(this.attrStackLabel)
                  .call(enter => {
                    enter
                      .append('rect')
                      .attr('class', 'c-graph-stack__bg')
                      .call(this.attrStackLabelBg);
                  })
                  .call(enter => {
                    enter
                      .append('text')
                      .attr('class', 'c-graph-stack__text')
                      .call(this.attrStackLabelText);
                  });
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrStackLabel)
                  .call(update => {
                    update.select('rect').call(this.attrStackLabelBg);
                  })
                  .call(update => {
                    update.select('text').call(this.attrStackLabelText);
                  });
              },
              exit => {
                return exit.remove();
              }
            );
        });
    },

    resize() {
      this.setSize();

      if (!this.isDrawed) {
        return;
      }
      this.setData();
      this.setScale();

      this.el.gStackMask
        .selectAll('clipPath')
        .select('rect')
        .call(this.attrStackMask);

      this.el.gStackBar
        .selectAll('g')
        .selectAll('rect')
        .call(this.attrStackBar);

      this.el.gStackLabel
        .selectAll('.c-graph-stack__label')
        .selectAll('g')
        .call(this.attrStackLabel);

      this.$refs.gAxisX.resize();
      this.$refs.gBgY.resize();
    }
  }
};
</script>
